/**
 * Shared plumbing for a choice field's optional "Other, please specify"
 * choice (`TypedFormField.allowOther`). See docs/form-design-gaps_findings.md
 * ("Chosen model") for why this is a transient sentinel rather than a
 * companion field: the typed answer is stored as the field's own value, and
 * this sentinel exists only to represent "Other is the selected choice but
 * nothing has been typed yet" in a renderer's live state. It must never reach
 * a submission.
 *
 * Every renderer that offers `allowOther` (the hosted page, the default
 * embed's vanilla runtime, and the opt-in React embed) imports this instead
 * of inlining its own copy of the sentinel or the predicate below.
 */
export const OTHER_OPTION_VALUE = "__other__";

/**
 * True when `value` represents "Other" as the active choice: the sentinel
 * itself, or any non-empty value that matches none of the field's listed
 * option values (a typed answer, or a previously-submitted draft being
 * reopened).
 */
export function isOtherValue(
  value: unknown,
  optionValues: ReadonlySet<string> | readonly string[],
): boolean {
  if (typeof value !== "string" || value === "") return false;
  if (value === OTHER_OPTION_VALUE) return true;
  const values =
    optionValues instanceof Set ? optionValues : new Set(optionValues);
  return !values.has(value);
}

/**
 * The sentinel must never reach a submission: a visitor who picked "Other"
 * and submitted before typing anything would otherwise send the literal
 * string "__other__" as their answer. Called on a field's live value right
 * before it is copied into a submission payload; a bare match becomes "" (so
 * the required rule treats it as unanswered), and an array drops it entirely
 * (so it reads as "Other was never checked", same as any other unchecked box
 * with nothing typed).
 */
export function stripOtherSentinel(value: unknown): unknown {
  if (value === OTHER_OPTION_VALUE) return "";
  if (Array.isArray(value)) {
    return value.filter((v) => v !== OTHER_OPTION_VALUE);
  }
  return value;
}
